/**
 * Created by neo on 18.01.17.
 */

import { makeAutoObservable, toJS, set, remove } from 'mobx';
import { v4 as UUID } from 'uuid';
import { LocalizedEntity, createLocalizedEntity } from '../../../LocalizedEntity';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';
import { PhaseConfiguration, createPhaseConfiguration } from './PhaseConfiguration';
import { ConditionalMedia, createConditionalMedia } from '../ConditionalMedia';
import { Retry } from '../../../../Utils/Retry';
import { Pageable } from '../../../Interfaces/Pageable';
import { PipelineContext, createPipelineContext } from '../../PipelineContext';
import { CoachWorkout, createCoachWorkout } from '../../Program/Workout/CoachWorkout';

export type WorkoutTemplateQueryRequest = Pageable & {
  name?: string;
  tags?: string[];
  archived?: boolean;
  phaseScriptId?: string;
};

export type WorkoutTemplate = LocalizedEntity & {
  templateName?: string;
  type: string;
  medias: ConditionalMedia[];
  params: { [key: string]: any };
  tags: string[];
  phaseConfigurations: PhaseConfiguration[];
  minDurationInMinutes?: number;
  maxDurationInMinutes?: number;
  readonly isNew: boolean;
  readonly nameValid: boolean;
  readonly valid: boolean;
  readonly invalid: boolean;
  readonly tagMap: any;
  copy(data?: any): WorkoutTemplate;
  delete(): Promise<void>;
  setParam(paramName: string, value?: any): void;
  removeParam(paramName: string): void;
  save(): Promise<WorkoutTemplate>;
  execute(context?: PipelineContext): Promise<CoachWorkout>;
};

export function createWorkoutTemplate(json?: any): WorkoutTemplate {
  const base = createLocalizedEntity(json);
  const template: WorkoutTemplate = makeAutoObservable({
    ...base,
    type: json?.type ?? 'gym_strength',
    templateName: json?.templateName,
    medias: (json?.medias ?? []).map((m: any) => createConditionalMedia(m)),
    name: (json?.name || []).map((v: any) => createLocalizedValue(v)),
    tags: json?.tags || [],
    params: json?.params ?? {},
    phaseConfigurations: (json?.phaseConfigurations ?? []).map((p: any) => createPhaseConfiguration(p)),
    minDurationInMinutes: json?.minDurationInMinutes,
    maxDurationInMinutes: json?.maxDurationInMinutes,
    description: (json?.description || []).map((v: any) => createLocalizedValue(v)),

    get isNew(): boolean {
      return !this.id || this.id.length <= 0;
    },

    get nameValid(): boolean {
      if (this.defaultName) {
        return this.defaultName.trim().length > 0;
      }
      return false;
    },

    get valid(): boolean {
      return this.nameValid;
    },

    get invalid(): boolean {
      return !this.valid;
    },

    get tagMap(): any {
      return this.tags
        .map((t) => t.split(':'))
        .reduce((result: any, tag: string[]) => {
          const entry = result[tag[0]] || [];
          entry.push(tag[1]);
          result[tag[0]] = entry.sort();
          return result;
        }, {});
    },

    toJS(replaceId: boolean = false) {
      return {
        ...(function() { const { id, createdAt, updatedAt, createdBy, updatedBy, name, description } = base; return { id: replaceId ? UUID() : id, createdAt, updatedAt, createdBy, updatedBy, name, description }; })(),
        templateName: this.templateName,
        type: this.type,
        tags: toJS(this.tags),
        phaseConfigurations: this.phaseConfigurations.map((p) => p.toJS(replaceId)),
        minDurationInMinutes: this.minDurationInMinutes,
        maxDurationInMinutes: this.maxDurationInMinutes,
        params: toJS(this.params),
        medias: this.medias.map((m) => m),
      };
    },

    copy(data?: any): WorkoutTemplate {
      return createWorkoutTemplate(Object.assign(this.toJS(true), data || {}));
    },

    delete() {
      return HttpBackend.delete(`/coach/program/template/workout/${this.id}`);
    },

    setParam(paramName: string, value?: any) {
      this.params = this.params ?? {};
      set(this.params, paramName, value);
    },

    removeParam(paramName: string) {
      this.params = this.params ?? {};
      remove(this.params, paramName);
    },

    save(): Promise<WorkoutTemplate> {
      return HttpBackend.post('/coach/program/template/workout', this).then(() => this);
    },

    execute(context: PipelineContext = createPipelineContext()): Promise<CoachWorkout> {
      return Retry.tryTimes(() =>
        HttpBackend.post(`/coach/program/template/workout/generate`, {
          workoutTemplateId: this.id,
          context: context,
        }).then((result) => createCoachWorkout(result)),
      );
    },
  });

  return template;
}

export const WorkoutTemplateApi = {
  async find(params: WorkoutTemplateQueryRequest = {}): Promise<WorkoutTemplate[]> {
    const res = await HttpBackend.get('/coach/program/template/workout', params);
    if (res) {
      return res.map((w: any) => createWorkoutTemplate(w));
    }
    return [];
  },

  async list(params: WorkoutTemplateQueryRequest = {}): Promise<WorkoutTemplate[]> {
    const res = await HttpBackend.get('/coach/program/template/workout/list', params);
    if (res) {
      return res.map((w: any) => createWorkoutTemplate(w));
    }
    return [];
  },

  async get(workoutId: string): Promise<WorkoutTemplate | undefined> {
    const res = await HttpBackend.get(`/coach/program/template/workout/${workoutId}`);
    if (res) {
      return createWorkoutTemplate(res);
    }
    return undefined;
  },

  async getAll(workoutIds: string[]): Promise<WorkoutTemplate[]> {
    const result = await Promise.all(workoutIds.map((w) => WorkoutTemplateApi.get(w)));
    return result.filter((w) => !!w) as WorkoutTemplate[];
  },
};
